import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useTableContext } from '@/contexts/table-context';

import { isHiresToDateEligible } from './eligibility';

const STORAGE_KEY = 'source.campaigns.hiresToDate';

interface HiresToDateValue {
  /** The range ended before today, so the switch can turn on. */
  isEligible: boolean;
  /** The switch is on and applies to the current range. */
  isOn: boolean;
  setOn: (next: boolean) => void;
  /** The day hires count to when the switch is on. */
  today: Date;
}

// Outside the All Campaigns page, nothing counts hires to date.
const HiresToDateContext = createContext<HiresToDateValue>({
  isEligible: false,
  isOn: false,
  setOn: () => {},
  today: new Date(),
});

function readStoredChoice(): boolean {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * The Hires to date choice for the All Campaigns cards and table. It lasts for
 * the browser session: a range that includes today suspends it, and a past
 * range restores it.
 */
export function HiresToDateProvider({ children }: { children: ReactNode }) {
  const { dateRange } = useTableContext();
  const [today] = useState(() => new Date());
  const [choice, setChoice] = useState(readStoredChoice);

  const setOn = useCallback((next: boolean) => {
    setChoice(next);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // A blocked sessionStorage keeps the choice for this page only.
    }
  }, []);

  const isEligible = isHiresToDateEligible(dateRange, today);
  const value = useMemo(
    () => ({ isEligible, isOn: isEligible && choice, setOn, today }),
    [isEligible, choice, setOn, today],
  );

  return (
    <HiresToDateContext.Provider value={value}>
      {children}
    </HiresToDateContext.Provider>
  );
}

export function useHiresToDate() {
  return useContext(HiresToDateContext);
}
